import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './TicketsPage.scss';
import UIButton from '@/components/shared/UIButton';
import UIPageTitle from '@/components/shared/UIPageTitle';
import TicketsTransactionsSection from '@/components/tenant/Tickets/TicketsTransactionsSection/TicketsTransactionsSection';
import QuickHelpSection from '@/components/tenant/QuickHelpSection/QuickHelpSection';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import { TicketDictionariesResponse } from '@/types/tickets';
import UIToast from '@/components/shared/UIToast';

const STORAGE_KEYS = {
  SELECTED_STATUS: 'tenant_tickets_selected_status',
  SELECTED_CATEGORY: 'tenant_tickets_selected_category',
} as const;

const saveFiltersToStorage = (status: string, category: string) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SELECTED_STATUS, status);
    localStorage.setItem(STORAGE_KEYS.SELECTED_CATEGORY, category);
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

const loadFiltersFromStorage = () => {
  try {
    return {
      status:
        localStorage.getItem(STORAGE_KEYS.SELECTED_STATUS) || 'all-statuses',
      category:
        localStorage.getItem(STORAGE_KEYS.SELECTED_CATEGORY) ||
        'all-categories',
    };
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
    return {
      status: 'all-statuses',
      category: 'all-categories',
    };
  }
};

const BackToDashboardButton = () => {
  const navigate = useNavigate();
  return (
    <UIButton
      outline={true}
      variant="primary"
      size="medium"
      onClick={() => navigate('/')}
    >
      Back to Home
    </UIButton>
  );
};

const TicketsPage = () => {
  const savedFilters = loadFiltersFromStorage();
  const [selectedStatus, setSelectedStatus] = useState(savedFilters.status);
  const [selectedCategory, setSelectedCategory] = useState(
    savedFilters.category,
  );
  const { data: dictionaries } =
    useGetDictionaries<TicketDictionariesResponse>('tickets');
  const [toastMessage, setToastMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    saveFiltersToStorage(selectedStatus, selectedCategory);
  }, [selectedStatus, selectedCategory]);

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleTicketCreated = useCallback(() => {
    setToastMessage('Ticket created successfully!');
    setRefreshKey((prev) => prev + 1); // Trigger refresh in TicketsTransactionsSection
  }, []);

  return (
    <>
      <UIPageTitle title="Tickets" action={<BackToDashboardButton />} />
      <div className="TicketsPage__content">
        <QuickHelpSection
          categories={dictionaries?.ticketCategories || []}
          onTicketCreated={handleTicketCreated}
          dictionarySource="tickets"
        />
        <TicketsTransactionsSection
          key={refreshKey} // Use key to force re-mount and data refetch
          selectedStatus={selectedStatus}
          selectedCategory={selectedCategory}
          onStatusChange={handleStatusChange}
          onCategoryChange={handleCategoryChange}
        />
      </div>
      {toastMessage && (
        <UIToast message={toastMessage} onClose={() => setToastMessage('')} />
      )}
    </>
  );
};

export default TicketsPage;
